import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Accordion, AccordionActions, AccordionDetails, AccordionSummary} from '@mui/material';
import ExpandedView from './ExpandedView/ExpandedView';
import Title from './Title/Title';
import AgentNotesContainer from 'components/AgentNotesContainer';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import {comparableTypes, reportTypes} from 'lib/PropertyUtils';

const ComparablePropertyAccordion = (
  {
    advertisementDescription,
    property,
    active,
    comparableType,
    featured,
    featureFlags,
    index,
    isNZ,
    pageContext,
    reportType,
    selected,
    selectedView,
    settings,
    onImageSelect,
    onUpload,
    onDelete,
    onSelectChange,
    onAddFeatured,
    onFetchAdvertisementDescription,
    onRemoveFeatured,
    onRemoveSelected
  }) => {
  const [expanded, setExpanded] = useState(active);

  useEffect(() => {
    setExpanded(active);
  }, [active]);

  const updateIndex = (context) => {
    return (currentImageIndex) => {
      onImageSelect(currentImageIndex);
      if (context) {
        AnalyticsGateway.sendEvent({
          eventName: `Navigate ${context} Carousel`,
          properties: [
            {label: 'Page', value: pageContext},
            {label: 'Object Context', value: 'Comparable Property'},
            {label: 'Property ID', value: property.propertyId},
          ],
        });
      }
    };
  };

  const handleOnChange = (event, isExpanded) => {
    return setExpanded(isExpanded);
  };

  return (
    <Accordion
        expanded={expanded}
        classes={{
            root: `ComparablePropertyAccordion ui segment comparable-property ${selected ? 'selected-comparable-property' : ''}`
        }}
        TransitionProps={{
            timeout: 0
        }}
        onChange={handleOnChange}
    >
      <AccordionSummary
        aria-label="Expand"
        aria-controls="additional-actions1-content"
        id="additional-actions1-header"
        classes={{
          root: 'title',
          content: 'content',
          expanded: 'expanded'
        }}
      >
        <Title
            isSelected={selected}
            isSelectView={selectedView}
            property={property}
            comparableType={comparableType}
            featured={featured}
            index={index}
            reportType={reportType}
            onAddFeatured={onAddFeatured}
            onRemoveFeatured={onRemoveFeatured}
            onRemoveSelected={onRemoveSelected}
            onSelectChange={onSelectChange}
        />
      </AccordionSummary>
      <AccordionDetails classes={{root: 'expanded-details'}}>
        <ExpandedView advertisementDescription={advertisementDescription}
                      comparableType={comparableType}
                      featureFlags={featureFlags}
                      isNZ={isNZ}
                      pageContext={pageContext}
                      property={property}
                      settings={settings}
                      updateIndex={updateIndex}
                      onDelete={onDelete}
                      onFetchAdvertisementDescription={onFetchAdvertisementDescription}
                      onUpload={onUpload}/>
      </AccordionDetails>
      <AccordionActions data-testid="property-actions" classes={{root: 'item'}}>
        <AgentNotesContainer propertyId={property.propertyId}
                             comparableType={comparableType}
                             showFeatureProperty={!selectedView}
        />
      </AccordionActions>
    </Accordion>
  );
};

ComparablePropertyAccordion.propTypes = {
  advertisementDescription: PropTypes.string,
  property: PropTypes.shape({
    advertisementDescription: PropTypes.string,
    propertyId: PropTypes.number.isRequired,
    singleLineAddress: PropTypes.string.isRequired,
    bedrooms: PropTypes.number,
    bathrooms: PropTypes.number,
    carSpaces: PropTypes.number,
    floorArea: PropTypes.number,
    landArea: PropTypes.number,
    yearBuilt: PropTypes.number,
    capitalValue: PropTypes.number,
    saleDate: PropTypes.string,
    salePrice: PropTypes.number,
    daysOnMarket: PropTypes.number,
    firstPublishedPrice: PropTypes.string,
    latestPublishedPrice: PropTypes.string,
    propertyPhotoList: PropTypes.arrayOf(PropTypes.string.isRequired),
    uploadPhotoList: PropTypes.arrayOf(PropTypes.string.isRequired),
    propertyPhotoUriSelectedIndex: PropTypes.number,
  }).isRequired,
  active: PropTypes.bool.isRequired,
  comparableType: PropTypes.oneOf(comparableTypes).isRequired,
  featured: PropTypes.bool.isRequired,
  featureFlags: PropTypes.object,
  index: PropTypes.number,
  isNZ: PropTypes.bool.isRequired,
  pageContext: PropTypes.string.isRequired,
  reportType: PropTypes.oneOf(reportTypes).isRequired,
  settings: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  selectedView: PropTypes.bool,
  onImageSelect: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSelectChange: PropTypes.func.isRequired,
  onAddFeatured: PropTypes.func.isRequired,
  onFetchAdvertisementDescription: PropTypes.func.isRequired,
  onRemoveFeatured: PropTypes.func.isRequired,
  onRemoveSelected: PropTypes.func.isRequired
};

ComparablePropertyAccordion.defaultProps = {
  isNZ: false,
  propertyDetailsSpacing: 6,
  selectedView: false,
};

export default ComparablePropertyAccordion;
